import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { LibraryArticleRecord } from '../../types/libraryProvenance';
import { ARCHIVE_SOURCE_FILTER_OPTIONS } from '../../types/libraryProvenance';
import {
  resolveSourcePermissions,
  articleMatchesSavedFromFilter,
  isLitPortalSource,
} from '../../utils/sourcePermissions';
import { SourcePermissionBadges } from './SourcePermissionBadges';
import { ExternalLink, FolderOpen } from 'lucide-react';
import { cn } from '../../lib/utils';

interface LitArchiveTableProps {
  articles: LibraryArticleRecord[];
  onMoveToFolder?: (article: LibraryArticleRecord) => void;
  onOpenShelf?: (pmid: string) => void;
}

function formatSavedDate(savedAt?: string): string {
  if (!savedAt) return '—';
  return new Date(savedAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatFullTextStatus(status?: LibraryArticleRecord['full_text_status']): string {
  if (!status || status === 'unknown') return '—';
  return status.replace(/_/g, ' ');
}

const thClass =
  'px-3 py-2.5 font-medium whitespace-normal break-words leading-snug sticky top-0 bg-slate-50 z-10 border-b border-slate-200 align-top';
const tdClass = 'px-3 py-2.5 align-top text-slate-600 whitespace-normal break-words leading-snug';

/** Fixed column widths — content wraps instead of stretching the table. */
const COL = {
  title: 'w-[200px] max-w-[200px]',
  authors: 'w-[160px] max-w-[160px]',
  journal: 'w-[140px] max-w-[140px]',
  year: 'w-[64px] max-w-[64px]',
  source: 'w-[96px] max-w-[96px]',
  savedFrom: 'w-[104px] max-w-[104px]',
  folder: 'w-[120px] max-w-[120px]',
  pmid: 'w-[104px] max-w-[104px]',
  doi: 'w-[100px] max-w-[100px]',
  specialty: 'w-[88px] max-w-[88px]',
  fullText: 'w-[72px] max-w-[72px]',
  eligibility: 'w-[160px] max-w-[160px]',
  saved: 'w-[96px] max-w-[96px]',
  actions: 'w-[80px] max-w-[80px]',
} as const;

function colTh(key: keyof typeof COL) {
  return cn(thClass, COL[key]);
}

function colTd(key: keyof typeof COL, extra?: string) {
  return cn(tdClass, COL[key], extra);
}

function ArchiveRowActions({
  article,
  onOpenShelf,
  onMoveToFolder,
  compact,
}: {
  article: LibraryArticleRecord;
  onOpenShelf?: (pmid: string) => void;
  onMoveToFolder?: (article: LibraryArticleRecord) => void;
  compact?: boolean;
}) {
  return (
    <div className={cn('flex gap-1', compact ? 'justify-end' : 'flex-col items-center')}>
      {onOpenShelf && (
        <Button
          variant="ghost"
          size={compact ? 'sm' : 'icon'}
          className={compact ? 'h-8 px-2' : 'h-7 w-7'}
          onClick={() => onOpenShelf(article.pmid)}
          title="View on shelf"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {compact && <span className="ml-1.5 text-xs">Shelf</span>}
        </Button>
      )}
      {onMoveToFolder && (
        <Button
          variant="ghost"
          size={compact ? 'sm' : 'icon'}
          className={compact ? 'h-8 px-2' : 'h-7 w-7'}
          onClick={() => onMoveToFolder(article)}
          title="Add to folder"
        >
          <FolderOpen className="w-3.5 h-3.5" />
          {compact && <span className="ml-1.5 text-xs">Folder</span>}
        </Button>
      )}
    </div>
  );
}

function LitArchiveMobileCard({
  article,
  onOpenShelf,
  onMoveToFolder,
}: {
  article: LibraryArticleRecord;
  onOpenShelf?: (pmid: string) => void;
  onMoveToFolder?: (article: LibraryArticleRecord) => void;
}) {
  const perms = resolveSourcePermissions(article);
  const savedDate = formatSavedDate(article.saved_at);

  return (
    <article
      className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
      data-testid={`litarchive-mobile-row-${article.pmid}`}
    >
      <div className="space-y-2">
        <h3 className="font-medium text-slate-900 leading-snug">{article.title}</h3>
        {article.authors && (
          <p className="text-xs text-slate-500 line-clamp-2">{article.authors}</p>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <div>
          <dt className="text-slate-400 uppercase tracking-wide text-[10px]">Journal</dt>
          <dd className="text-slate-700 mt-0.5">{article.journal || '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-400 uppercase tracking-wide text-[10px]">Year</dt>
          <dd className="text-slate-700 mt-0.5">{article.year || '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-400 uppercase tracking-wide text-[10px]">Source</dt>
          <dd className="text-slate-700 mt-0.5 capitalize">{article.source || '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-400 uppercase tracking-wide text-[10px]">Saved from</dt>
          <dd className="mt-0.5">
            <Badge variant="outline" className="text-[10px] font-normal">
              {isLitPortalSource(article) ? 'LitPortal' : 'LitCurrent'}
            </Badge>
          </dd>
        </div>
        <div>
          <dt className="text-slate-400 uppercase tracking-wide text-[10px]">Folder</dt>
          <dd className="text-slate-700 mt-0.5">{article.folder || 'Ungrouped'}</dd>
        </div>
        <div>
          <dt className="text-slate-400 uppercase tracking-wide text-[10px]">Saved</dt>
          <dd className="text-slate-700 mt-0.5">{savedDate}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-slate-400 uppercase tracking-wide text-[10px]">PMID</dt>
          <dd className="text-slate-700 mt-0.5 font-mono">{article.pmid || '—'}</dd>
        </div>
        {article.doi && (
          <div className="col-span-2">
            <dt className="text-slate-400 uppercase tracking-wide text-[10px]">DOI</dt>
            <dd className="text-slate-700 mt-0.5 font-mono break-all">{article.doi}</dd>
          </div>
        )}
        {article.specialty_name && (
          <div className="col-span-2">
            <dt className="text-slate-400 uppercase tracking-wide text-[10px]">Specialty</dt>
            <dd className="text-slate-700 mt-0.5">{article.specialty_name}</dd>
          </div>
        )}
        <div className="col-span-2">
          <dt className="text-slate-400 uppercase tracking-wide text-[10px] mb-1">Full text</dt>
          <dd className="text-slate-700 capitalize">{formatFullTextStatus(article.full_text_status)}</dd>
        </div>
      </dl>

      {perms.badges.length > 0 && (
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1.5">Eligibility</p>
          <SourcePermissionBadges badges={perms.badges} />
        </div>
      )}

      <ArchiveRowActions
        article={article}
        onOpenShelf={onOpenShelf}
        onMoveToFolder={onMoveToFolder}
        compact
      />
    </article>
  );
}

export const LitArchiveTable: React.FC<LitArchiveTableProps> = ({
  articles,
  onMoveToFolder,
  onOpenShelf,
}) => {
  const [query, setQuery] = React.useState('');
  const [folderFilter, setFolderFilter] = React.useState<string>('all');
  const [savedFromFilter, setSavedFromFilter] = React.useState<string>('all');

  const folders = React.useMemo(() => {
    const set = new Set(articles.map((a) => a.folder || 'Ungrouped'));
    return ['all', ...Array.from(set).sort()];
  }, [articles]);

  const filtered = React.useMemo(() => {
    return articles.filter((a) => {
      if (folderFilter !== 'all' && (a.folder || 'Ungrouped') !== folderFilter) return false;
      if (!articleMatchesSavedFromFilter(a, savedFromFilter)) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        a.title.toLowerCase().includes(q) ||
        a.journal?.toLowerCase().includes(q) ||
        a.authors?.toLowerCase().includes(q) ||
        a.pmid.includes(q) ||
        a.doi?.toLowerCase().includes(q) ||
        a.specialty_name?.toLowerCase().includes(q)
      );
    });
  }, [articles, query, folderFilter, savedFromFilter]);

  const emptyState = (
    <p className="px-4 py-12 text-center text-slate-400 text-sm">
      No articles match your filters.
    </p>
  );

  return (
    <div className="space-y-4 w-full min-w-0" data-testid="litarchive-table">
      <p className="text-sm text-slate-500">
        LitArchive manages the same LitHub data — folder and repository view with provenance filters.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
        <Input
          placeholder="Filter by title, journal, authors, PMID, DOI…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="sm:max-w-sm h-9"
          data-testid="litarchive-search"
        />
        <Select value={folderFilter} onValueChange={setFolderFilter}>
          <SelectTrigger className="w-full sm:w-[180px] h-9">
            <SelectValue placeholder="Folder" />
          </SelectTrigger>
          <SelectContent>
            {folders.map((f) => (
              <SelectItem key={f} value={f}>
                {f === 'all' ? 'All folders' : f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={savedFromFilter} onValueChange={setSavedFromFilter}>
          <SelectTrigger className="w-full sm:w-[180px] h-9">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {ARCHIVE_SOURCE_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-3" data-testid="litarchive-mobile">
        {filtered.length === 0
          ? emptyState
          : filtered.map((article) => (
              <LitArchiveMobileCard
                key={article.pmid}
                article={article}
                onOpenShelf={onOpenShelf}
                onMoveToFolder={onMoveToFolder}
              />
            ))}
      </div>

      {/* Desktop: full-width horizontally scrollable table */}
      <div
        className="hidden md:block -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8"
        data-testid="litarchive-desktop"
      >
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-max min-w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                <th className={colTh('title')}>Title</th>
                <th className={colTh('authors')}>Authors</th>
                <th className={colTh('journal')}>Journal</th>
                <th className={colTh('year')}>Year</th>
                <th className={colTh('source')}>Source</th>
                <th className={colTh('savedFrom')}>Saved from</th>
                <th className={colTh('folder')}>Folder</th>
                <th className={colTh('pmid')}>PMID</th>
                <th className={colTh('doi')}>DOI</th>
                <th className={colTh('specialty')}>Specialty</th>
                <th className={colTh('fullText')}>Full text</th>
                <th className={colTh('eligibility')}>Eligibility</th>
                <th className={colTh('saved')}>Saved</th>
                <th className={colTh('actions')}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={14}>{emptyState}</td>
                </tr>
              ) : (
                filtered.map((article) => {
                  const perms = resolveSourcePermissions(article);
                  const savedDate = formatSavedDate(article.saved_at);

                  return (
                    <tr
                      key={article.pmid}
                      className="border-b border-slate-100 hover:bg-slate-50/80"
                      data-testid={`litarchive-row-${article.pmid}`}
                    >
                      <td className={colTd('title', 'text-slate-900 font-medium')}>
                        {article.title}
                      </td>
                      <td className={colTd('authors')}>{article.authors || '—'}</td>
                      <td className={colTd('journal')}>{article.journal || '—'}</td>
                      <td className={colTd('year')}>{article.year || '—'}</td>
                      <td className={colTd('source', 'capitalize')}>{article.source || '—'}</td>
                      <td className={colTd('savedFrom')}>
                        <Badge variant="outline" className="text-[10px] font-normal whitespace-normal break-words">
                          {isLitPortalSource(article) ? 'LitPortal' : 'LitCurrent'}
                        </Badge>
                      </td>
                      <td className={colTd('folder')}>{article.folder || 'Ungrouped'}</td>
                      <td className={colTd('pmid', 'font-mono text-xs break-all')}>{article.pmid}</td>
                      <td className={colTd('doi', 'font-mono text-xs break-all')}>
                        {article.doi || '—'}
                      </td>
                      <td className={colTd('specialty')}>{article.specialty_name || '—'}</td>
                      <td className={colTd('fullText', 'capitalize')}>
                        {formatFullTextStatus(article.full_text_status)}
                      </td>
                      <td className={colTd('eligibility')}>
                        <SourcePermissionBadges badges={perms.badges} />
                      </td>
                      <td className={colTd('saved', 'text-xs')}>{savedDate}</td>
                      <td className={colTd('actions')}>
                        <ArchiveRowActions
                          article={article}
                          onOpenShelf={onOpenShelf}
                          onMoveToFolder={onMoveToFolder}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Scroll horizontally to see all columns
        </p>
      </div>

      <p className="text-xs text-slate-400">
        Showing {filtered.length} of {articles.length} articles
      </p>
    </div>
  );
};

export default LitArchiveTable;
